import { Checkbox } from "@/components/ui/checkbox-alt";
import { Label } from "@/components/ui/label";
import { cn, renderHtmlChoiceId, renderMarkdown } from "@/lib/utils";
import { useQuestion } from "@/store/question.context";
import { Choice, ChoiceType } from "@/types/questionnaries";
import { CheckedState } from "@radix-ui/react-checkbox";
import { FC, useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import ChoiceList from "./choice-list";
import FillablePart from "./fillable-part";

type MultipleChoiceItemrops = {
  choice: Choice;
  level: number;
  disabled?: boolean;
};

const MultipleChoiceItem: FC<MultipleChoiceItemrops> = ({
  choice,
  level,
  disabled,
}) => {
  const { updateChoice, isSuggestionApplied, setIsSuggestionApplied } =
    useQuestion();

  const [answerValue, setAnswerValue] = useState<string>(
    choice.type === ChoiceType.Fillable ? choice.answerValue : "",
  );

  const checked = choice.selected;

  const [debouncedAnswerValue] = useDebounce(answerValue, 500);

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const handleCheckedChange = (checked: CheckedState) => {
    const isChecked = checked === "indeterminate" ? true : checked;

    updateChoice({
      ...choice,
      selected: isChecked,
    });

    setIsSuggestionApplied(false);
  };

  const handleTextChange = (value: string) => {
    setAnswerValue(value);
    setIsSuggestionApplied(false);
  };

  // When the debouncedAnswerValue changes, save it to the choice object
  useEffect(() => {
    if (choice.type === ChoiceType.Fillable) {
      updateChoice({
        ...choice,
        answerValue: debouncedAnswerValue,
      });
    }
  }, [debouncedAnswerValue]);

  return (
    <li className="grid gap-3" style={{ paddingLeft: `${40 * level}px` }}>
      <div className="flex">
        <Checkbox
          id={renderHtmlChoiceId(choice.id)}
          checked={checked}
          onCheckedChange={handleCheckedChange}
          className={cn(
            isSuggestionApplied && "data-[state=checked]:bg-[#F0A000]",
          )}
          disabled={disabled}
        />

        <div className="grid grow">
          <Label
            htmlFor={renderHtmlChoiceId(choice.id)}
            className="pl-2 pt-0.5 font-normal leading-snug"
            dangerouslySetInnerHTML={{
              __html: renderMarkdown(choice.label),
            }}
          />

          {checked && choice.type === ChoiceType.Fillable && (
            <FillablePart
              choice={choice}
              inputRef={inputRef}
              answerValue={answerValue}
              handleTextChange={handleTextChange}
              disabled={disabled}
            />
          )}

          {checked &&
            choice.type === ChoiceType.Selectable &&
            choice.statement && (
              <p className="pl-2 text-sm font-medium text-muted-foreground">
                {choice.statement}
              </p>
            )}
        </div>
      </div>

      {checked && (
        <ChoiceList choice={choice} level={level + 1} disabled={disabled} />
      )}
    </li>
  );
};

export default MultipleChoiceItem;
